import { createContext, PropsWithChildren, useState } from "react";
import { BridgeEnv, ERROR_CODE_400, ERROR_CODE_401, TransferFlow } from "../typings";
import getAPI from "../utils/getAPI";
import sendError from "../utils/sendError";

export type AppProviderValue = ReturnType<typeof CreateProviderValue>;

export const CreateProviderValue = () => {
  const [isConfig, setIsConfig] = useState<boolean>(false);
  const [appEnv, setAppEnv] = useState<BridgeEnv>();
  const [isWebview, setIsWebview] = useState<boolean>(false);
  const [isAppLoading, setIsAppLoading] = useState<boolean>(true);
  const [partnerConfig, setPartnerConfig] = useState<string>();
  const [transferFlow, setTransferFlow] = useState<TransferFlow>();
  const [transferIntentId, setTransferIntentId] = useState<string>("");
  const [accessToken, setAccessToken] = useState<string>("");
  const [transferId, setTransferId] = useState<number | string>();
  const [clientId, setClientId] = useState<string>("");
  const [version, setVersion] = useState<"New" | "Old">("Old");
  const [parentUrl, setParentUrl] = useState<string>("*");

  const claimToken = async ({
    token,
    env,
    clientId: $clientId,
    origin,
  }: {
    token: string;
    env: BridgeEnv;
    clientId: string;
    origin: string;
  }): Promise<{ $transferIntentId?: string; $accessToken?: string }> => {
    setParentUrl(origin);
    try {
      const res = await getAPI(env)({
        url: `/auth/ott/claim/${token}?clientReference=${$clientId}`,
        method: "GET",
      });
      setAccessToken(res.data.data.jwt);
      const $transferIntentId = res.data.data.data.reference;
      setTransferIntentId($transferIntentId);
      return { $transferIntentId, $accessToken: res.data.data.jwt };
    } catch (e: any) {
      const errorMessage = e.response?.data.message;
      if (errorMessage === "Expired Resource") {
        sendError({ errorMessage: "The Launch Token is expired", errorCode: ERROR_CODE_401 }, false, origin);
      } else {
        sendError({ errorMessage: "Invalid token", errorCode: ERROR_CODE_400 }, false, origin);
      }
      return {};
    }
  };

  return {
    appEnv,
    setAppEnv,
    isConfig,
    setIsConfig,
    isAppLoading,
    setIsAppLoading,
    partnerConfig,
    setPartnerConfig,
    transferFlow,
    setTransferFlow,
    transferIntentId,
    setTransferIntentId,
    accessToken,
    setAccessToken,
    transferId,
    setTransferId,
    clientId,
    setClientId,
    isWebview,
    setIsWebview,
    claimToken,
    version,
    setVersion,
    parentUrl,
    setParentUrl,
  };
};

export const AppContext = createContext({} as AppProviderValue);

const AppProvider = (props: PropsWithChildren<any>) => {
  const providerValue = CreateProviderValue();
  return <AppContext.Provider value={providerValue}>{props.children}</AppContext.Provider>;
};

export default AppProvider;
